import {
  eventDiffersFromBaseline,
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "components/manualReview/contexts/ManualReviewEventContext";
import dayjs, { Dayjs } from "dayjs";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant, useUserUid } from "hooks/settings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  generatedApi,
  ReviewPeriodData,
  ReviewSlotRevision,
  useUnverifyPeriodMutation,
} from "src/store/api/generatedApi";
import {
  buildVerifyScope,
  classifyWindowVerification,
  eventStartsInGap,
  firstCrossingNeighbour,
  maxSequenceGapSecondsFromChunkSeconds,
  periodsTouchedByEdits,
  shouldRouteToSubmitEdits,
  verifyWindowSeedPeriods,
} from "../utils/editScope";
import {
  buildBringInRequest,
  resolveBringInChain,
} from "../utils/bringInChain";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import {
  useConfirmSaveWarnings,
  usePersistVerifiedPeriods,
  useReportChainCapped,
  useRunExclusively,
} from "./useVerifySaveGuards";
import { useVerificationPreview } from "../VerificationPreviewDialog";
import { useLinkedPeriodBringIn } from "../LinkedPeriodBringInDialog";
import { useVideoControlActions } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import { revisionToUnverify } from "../utils/reviewRevisions";
import { useReviewSaveError } from "./useReviewSaveError";
import { useConfirmDialog } from "contexts/confirmDialog";

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

interface UseManualReviewActionsProps {
  location: string;
  reviewPeriods: ReviewPeriodData[];
  viewingWindowStart: Dayjs;
  viewingWindowEnd: Dayjs;
  debouncedWindowStart: Dayjs;
  debouncedWindowEnd: Dayjs;
  isEventsFetching: boolean;
  allReviewPeriods: ReviewPeriodData[];
  verifyPeriods: (
    periods: ReviewPeriodData[],
    eventsInPeriods: ScrapEyesEventWithReviewData[],
    originalEventsInPeriods: ScrapEyesEventWithReviewData[],
    options?: {
      includeVerified?: boolean;
    }
  ) => Promise<void>;
  goToPeriod: (period: ReviewPeriodData) => void;
  verifyPeriodStarts?: string[];
}

export const useManualReviewActions = ({
  location,
  reviewPeriods,
  viewingWindowStart,
  viewingWindowEnd,
  debouncedWindowStart,
  debouncedWindowEnd,
  isEventsFetching,
  allReviewPeriods,
  verifyPeriods,
  goToPeriod,
  verifyPeriodStarts,
}: UseManualReviewActionsProps) => {
  const isEventsInSync =
    !isEventsFetching &&
    debouncedWindowStart.isSame(viewingWindowStart) &&
    debouncedWindowEnd.isSame(viewingWindowEnd);
  const { t } = useTenantTranslation();
  const { reservation } = useScrapEyeMetadata();
  const chunkSeconds = reservation.chunk_seconds;
  const maxSequenceGapSeconds =
    maxSequenceGapSecondsFromChunkSeconds(chunkSeconds);
  const eventContext = useManualReviewEvents();
  const userUid = useUserUid();
  const tenantName = useTenant();
  const [unverifyPeriodMutation] = useUnverifyPeriodMutation();
  const confirm = useConfirmDialog();
  const reportReviewSaveError = useReviewSaveError();
  const previewVerification = useVerificationPreview();
  const bringInPeriod = useLinkedPeriodBringIn();
  const { pause: pauseVideo } = useVideoControlActions();
  const [isEditMode, setIsEditMode] = useState(false);
  // True for a whole bring-in chain, not per hop: the modal closes and reopens between
  // hops, and a background player torn down on each of those would rebuild twenty times
  // over a long chain.
  const [isBringInChainRunning, setIsBringInChainRunning] = useState(false);

  // The bring-in chain edits events through the modal mid-flow, so the loop and
  // the final payload must read the latest events, not a snapshot captured before
  // the first hop.
  const allEventsRef = useRef(eventContext.allEvents);
  allEventsRef.current = eventContext.allEvents;
  const modifiedEventIdsRef = useRef(eventContext.modifiedEventIds);
  modifiedEventIdsRef.current = eventContext.modifiedEventIds;
  const originalEventsByIdRef = useRef(eventContext.originalEventsById);
  originalEventsByIdRef.current = eventContext.originalEventsById;

  const runExclusively = useRunExclusively();

  // A start sits in a gap when it is inside the window but inside no review
  // period — the backend creates a review period on demand for that chunk, so
  // events added to footage the model skipped can still be saved.
  const startIsInGapWithinWindow = useCallback(
    (start: Dayjs): boolean =>
      start.isSameOrAfter(debouncedWindowStart) &&
      start.isBefore(debouncedWindowEnd) &&
      !reviewPeriods.some((period) => {
        const periodStart = dayjs(period.start);
        const periodEnd = dayjs(period.end);
        return start.isSameOrAfter(periodStart) && start.isBefore(periodEnd);
      }),
    [debouncedWindowStart, debouncedWindowEnd, reviewPeriods]
  );

  const overlapsWindow = useCallback(
    (event: ScrapEyesEventWithReviewData): boolean =>
      event.start.isBefore(debouncedWindowEnd) &&
      event.end.isAfter(debouncedWindowStart),
    [debouncedWindowStart, debouncedWindowEnd]
  );

  const windowSlice = useCallback(
    (
      events: ScrapEyesEventWithReviewData[],
      originalEventsById: ReadonlyMap<string, ScrapEyesEventWithReviewData>
    ) => ({
      events: events.filter(overlapsWindow),
      originalEvents: [...originalEventsById.values()].filter(overlapsWindow),
    }),
    [overlapsWindow]
  );

  const windowFocusEvents = useCallback(
    () => windowSlice(allEventsRef.current, originalEventsByIdRef.current),
    [windowSlice]
  );

  const reviewPeriodsInWindow = useMemo(
    () =>
      reviewPeriods.filter((period) => {
        const periodStart = dayjs(period.start);
        const periodEnd = dayjs(period.end);
        return (
          periodStart.isBefore(debouncedWindowEnd) &&
          periodEnd.isAfter(debouncedWindowStart)
        );
      }),
    [reviewPeriods, debouncedWindowStart, debouncedWindowEnd]
  );

  const unverifiedPeriodsInWindow = useMemo(
    () => reviewPeriodsInWindow.filter((period) => period.verified_at == null),
    [reviewPeriodsInWindow]
  );

  // Warm the cache for the first bring-in hop the verify flow would open, using the same
  // link resolution and focus window. This is the modal's probe — the padded report it
  // resolves its labellers from. The merge it then renders cannot be warmed from here,
  // because the periods to exclude are only known from the probe's response.
  const prefetchLabellerTimelines = generatedApi.usePrefetch(
    "getLabellerTimelinesReport"
  );
  const { allEvents, originalEventsById } = eventContext;
  useEffect(() => {
    if (!isEventsInSync || unverifiedPeriodsInWindow.length === 0) return;
    const seedIds = new Set(
      unverifiedPeriodsInWindow.map((period) => period.id)
    );
    const { events, originalEvents } = windowSlice(
      allEvents,
      originalEventsById
    );
    const firstHop = firstCrossingNeighbour({
      focusEvents: events,
      focusOriginalEvents: originalEvents,
      eventDiffersFromOriginal: eventDiffersFromBaseline,
      maxSequenceGapSeconds,
      dayPeriods: allReviewPeriods,
      windowStart: debouncedWindowStart,
      windowEnd: debouncedWindowEnd,
      periodStart: (period) => dayjs(period.start),
      periodEnd: (period) => dayjs(period.end),
      periodId: (period) => period.id,
      originPeriodIds: seedIds,
      scopePeriodIds: seedIds,
      resolvedPeriodIds: seedIds,
    });
    if (!firstHop) return;
    const request = buildBringInRequest(
      firstHop,
      allReviewPeriods,
      debouncedWindowStart,
      debouncedWindowEnd,
      { index: 1, canGoBack: false, canBypassPage: true }
    );
    if (!request.outOfWindow) return;
    prefetchLabellerTimelines({
      tenantName,
      location,
      windowStart: request.focusStart
        .subtract(chunkSeconds, "second")
        .toISOString(),
      windowEnd: request.focusEnd.add(chunkSeconds, "second").toISOString(),
    });
  }, [
    isEventsInSync,
    unverifiedPeriodsInWindow,
    allEvents,
    originalEventsById,
    windowSlice,
    allReviewPeriods,
    debouncedWindowStart,
    debouncedWindowEnd,
    prefetchLabellerTimelines,
    tenantName,
    location,
    chunkSeconds,
    maxSequenceGapSeconds,
  ]);

  const bringInLinkedPeriods = useCallback(
    (seedPeriods: ReviewPeriodData[]) => {
      // Most verifies resolve no linked period at all, and those must leave the page's
      // video alone. The chain is only under way once it opens its first hop.
      let chainStarted = false;
      return resolveBringInChain({
        seedPeriods,
        maxSequenceGapSeconds,
        allReviewPeriods,
        windowStart: debouncedWindowStart,
        windowEnd: debouncedWindowEnd,
        seedFocusEvents: windowFocusEvents,
        bringInPeriod: (request) => {
          if (!chainStarted) {
            chainStarted = true;
            setIsBringInChainRunning(true);
            // The shared clock holds while the tiles are gone, so the position
            // survives the chain. The pause is what brings the page back still, on
            // the frame it left.
            pauseVideo();
          }
          return bringInPeriod(request);
        },
      });
    },
    [
      bringInPeriod,
      windowFocusEvents,
      allReviewPeriods,
      debouncedWindowStart,
      debouncedWindowEnd,
      maxSequenceGapSeconds,
      pauseVideo,
    ]
  );

  // Resolves the bring-in chain for the periods a save verifies, then builds the
  // event payload. The modal may have edited events during the chain, so the payload
  // reads the latest window events rather than a pre-chain snapshot. `capped` is
  // passed straight through from the chain — nothing here is safe to save from it.
  const resolveChainScope = useCallback(
    async ({
      seedPeriods,
      originalEvents,
    }: {
      seedPeriods: ReviewPeriodData[];
      originalEvents: ScrapEyesEventWithReviewData[];
    }): Promise<
      | {
          outcome: "resolved";
          periods: ReviewPeriodData[];
          eventsInScope: ScrapEyesEventWithReviewData[];
          originalEventsInScope: ScrapEyesEventWithReviewData[];
          reviewSlotRevisions?: ReviewSlotRevision[];
        }
      | { outcome: "capped" }
    > => {
      try {
        const chainResult = await bringInLinkedPeriods(seedPeriods);
        if (chainResult.outcome === "capped") {
          return { outcome: "capped" };
        }
        const { periods, broughtInEvents, broughtInOriginalEvents } =
          chainResult;
        const { eventsInScope, originalEventsInScope } = buildVerifyScope({
          periods,
          currentEvents: windowFocusEvents().events,
          originalEvents,
          broughtInEvents,
          broughtInOriginalEvents,
          periodStart: (period) => dayjs(period.start),
          periodEnd: (period) => dayjs(period.end),
          startIsInGap: startIsInGapWithinWindow,
        });
        return {
          outcome: "resolved",
          periods,
          eventsInScope,
          originalEventsInScope,
          reviewSlotRevisions: chainResult.reviewSlotRevisions,
        };
      } finally {
        setIsBringInChainRunning(false);
      }
    },
    [bringInLinkedPeriods, windowFocusEvents, startIsInGapWithinWindow]
  );

  const confirmSaveWarnings = useConfirmSaveWarnings();
  const persistVerifiedPeriods = usePersistVerifiedPeriods(verifyPeriods);
  const reportChainCapped = useReportChainCapped();

  const hasGapEdits = useMemo(
    () =>
      eventStartsInGap({
        events: eventContext.allEvents.filter(
          (event) =>
            event.start.isBefore(debouncedWindowEnd) &&
            event.end.isAfter(debouncedWindowStart)
        ),
        originalEvents: Array.from(eventContext.originalEventsById.values()),
        startIsInGap: startIsInGapWithinWindow,
      }),
    [
      eventContext.allEvents,
      eventContext.originalEventsById,
      debouncedWindowStart,
      debouncedWindowEnd,
      startIsInGapWithinWindow,
    ]
  );

  const verifyWindowAndGoToNextImpl = useCallback(async () => {
    if (!isEventsInSync) return;

    const originalEvents = Array.from(eventContext.originalEventsById.values());

    if (
      unverifiedPeriodsInWindow.length > 0 ||
      hasGapEdits ||
      (verifyPeriodStarts?.length ?? 0) > 0
    ) {
      const focus = windowFocusEvents();
      const chainScope = await resolveChainScope({
        seedPeriods: verifyWindowSeedPeriods({
          periods: reviewPeriodsInWindow,
          isVerified: (period) => period.verified_at != null,
          periodStart: (period) => dayjs(period.start),
          periodEnd: (period) => dayjs(period.end),
          currentEvents: focus.events,
          originalEvents: focus.originalEvents,
          modifiedEventIds: modifiedEventIdsRef.current,
        }),
        originalEvents,
      });
      if (chainScope.outcome === "capped") {
        await reportChainCapped();
        return;
      }
      const {
        periods: periodsToVerify,
        eventsInScope,
        originalEventsInScope,
      } = chainScope;

      if (!(await confirmSaveWarnings(eventsInScope))) {
        return;
      }

      if (
        !(await persistVerifiedPeriods(
          periodsToVerify,
          eventsInScope,
          originalEventsInScope,
          verifyPeriodStarts,
          chainScope.reviewSlotRevisions
        ))
      ) {
        return;
      }
      eventContext.rememberSavedEvents(eventsInScope);
      eventContext.clearLocalChanges();
    }

    const sortedPeriods = sorted(
      allReviewPeriods,
      (period: ReviewPeriodData) => period.start
    );

    const candidatePeriods = sortedPeriods.filter(
      (period: ReviewPeriodData) => {
        const periodEnd = dayjs(period.end);
        return (
          periodEnd.isAfter(viewingWindowEnd) && period.verified_at == null
        );
      }
    );

    const nextPeriod =
      candidatePeriods.find(
        (period: ReviewPeriodData) => period.assigned_to_id === userUid
      ) ?? candidatePeriods[0];

    if (nextPeriod) {
      goToPeriod(nextPeriod);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use specific properties; the whole context object is unstable
  }, [
    reviewPeriodsInWindow,
    unverifiedPeriodsInWindow,
    windowFocusEvents,
    hasGapEdits,
    viewingWindowEnd,
    isEventsInSync,
    goToPeriod,
    eventContext.clearLocalChanges,
    eventContext.rememberSavedEvents,
    eventContext.originalEventsById,
    allReviewPeriods,
    userUid,
    confirmSaveWarnings,
    persistVerifiedPeriods,
    reportChainCapped,
    resolveChainScope,
    verifyPeriodStarts,
  ]);

  const hasVerifiedPeriods = useMemo(
    () => reviewPeriods.some((period) => period.verified_at != null),
    [reviewPeriods]
  );

  const hasEdits = eventContext.hasUnsavedChanges;

  const handleUnverifyWindow = useCallback(async () => {
    const verifiedPeriods = reviewPeriods.filter(
      (period) => period.verified_at != null
    );
    if (verifiedPeriods.length === 0) return;
    if (
      !(await confirm({
        title: t("confirm"),
        message: t("unverifyWarning"),
      }))
    ) {
      return;
    }
    try {
      const periodIds = verifiedPeriods.map((period) =>
        revisionToUnverify({
          period,
          snapshot: eventContext.reviewSlotRevisions,
        })
      );
      await Promise.all(
        periodIds.map((periodId) =>
          unverifyPeriodMutation({ tenantName, periodId }).unwrap()
        )
      );
    } catch (error) {
      await reportReviewSaveError(error);
    }
  }, [
    reviewPeriods,
    unverifyPeriodMutation,
    tenantName,
    confirm,
    t,
    eventContext.reviewSlotRevisions,
    reportReviewSaveError,
  ]);

  const enterEditMode = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const cancelEditModeImpl = useCallback(async () => {
    if (eventContext.modifiedEventIds.size > 0) {
      const confirmed = await confirm({
        title: t("confirm"),
        message: t("discardEditsWarning"),
      });
      if (!confirmed) return;
    }
    eventContext.clearLocalChanges();
    setIsEditMode(false);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use specific properties; the whole context object is unstable
  }, [
    eventContext.modifiedEventIds,
    eventContext.clearLocalChanges,
    confirm,
    t,
  ]);

  const submitEditsImpl = useCallback(async () => {
    if (!isEventsInSync) return;

    const eventsInWindow = eventContext.allEvents.filter(
      (event: ScrapEyesEventWithReviewData) => {
        return (
          event.start.isBefore(debouncedWindowEnd) &&
          event.end.isAfter(debouncedWindowStart)
        );
      }
    );

    const modifiedEvents = eventsInWindow.filter((event) =>
      eventContext.modifiedEventIds.has(event.id)
    );

    const originalEventsInWindow = Array.from(
      eventContext.originalEventsById.values()
    ).filter(
      (event) =>
        event.start.isBefore(debouncedWindowEnd) &&
        event.end.isAfter(debouncedWindowStart)
    );

    const periodsToVerify = periodsTouchedByEdits({
      periods: reviewPeriodsInWindow,
      periodStart: (period) => dayjs(period.start),
      periodEnd: (period) => dayjs(period.end),
      currentEvents: eventsInWindow,
      originalEvents: originalEventsInWindow,
      modifiedEventIds: eventContext.modifiedEventIds,
    });

    const hasGapEdits = modifiedEvents.some((event) =>
      startIsInGapWithinWindow(event.start)
    );

    if (periodsToVerify.length === 0 && !hasGapEdits) return;

    const chainScope = await resolveChainScope({
      seedPeriods: periodsToVerify,
      originalEvents: originalEventsInWindow,
    });
    if (chainScope.outcome === "capped") {
      await reportChainCapped();
      return;
    }
    const {
      periods: expandedPeriods,
      eventsInScope,
      originalEventsInScope,
    } = chainScope;

    if (!(await confirmSaveWarnings(eventsInScope))) {
      return;
    }

    const reVerifiedPeriodIds = new Set(
      expandedPeriods
        .filter((period) => period.verified_at != null)
        .map((period) => period.id)
    );
    // The preview lists in-window periods plus any brought-in off-edge periods, so
    // the reviewer sees every period the save will verify, not just the visible ones.
    const offEdgePeriods = expandedPeriods.filter(
      (period) =>
        !reviewPeriodsInWindow.some((inWindow) => inWindow.id === period.id)
    );
    const verificationChanges = classifyWindowVerification({
      windowPeriods: [...reviewPeriodsInWindow, ...offEdgePeriods],
      periodStart: (period) => dayjs(period.start),
      periodEnd: (period) => dayjs(period.end),
      isVerified: (period) => period.verified_at != null,
      isReVerified: (period) => reVerifiedPeriodIds.has(period.id),
      correctedEventStarts: eventsInScope.map((event) => event.start),
    });
    const willChangeVerification = verificationChanges.some(
      (classified) => classified.change !== "unchanged"
    );
    const previewPeriods = verificationChanges.map((classified) => ({
      start: dayjs(classified.period.start),
      end: dayjs(classified.period.end),
      change: classified.change,
    }));
    // Brought-in periods sit outside the viewing window, so widen the preview
    // timeline to span every period and event the save touches, not just the
    // visible window.
    const previewStart = [
      ...previewPeriods.map((period) => period.start),
      ...eventsInScope.map((event) => event.start),
    ].reduce(
      (earliest, candidate) =>
        candidate.isBefore(earliest) ? candidate : earliest,
      debouncedWindowStart
    );
    const previewEnd = [
      ...previewPeriods.map((period) => period.end),
      ...eventsInScope.map((event) => event.end),
    ].reduce(
      (latest, candidate) => (candidate.isAfter(latest) ? candidate : latest),
      debouncedWindowEnd
    );
    if (
      willChangeVerification &&
      !(await previewVerification({
        windowStart: previewStart,
        windowEnd: previewEnd,
        periods: previewPeriods,
        events: eventsInScope,
        modifiedEventIds: modifiedEventIdsRef.current,
      }))
    ) {
      return;
    }

    if (
      !(await persistVerifiedPeriods(
        expandedPeriods,
        eventsInScope,
        originalEventsInScope,
        undefined,
        chainScope.reviewSlotRevisions
      ))
    ) {
      return;
    }
    eventContext.rememberSavedEvents(eventsInScope);
    eventContext.clearLocalChanges();
    setIsEditMode(false);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use specific properties; the whole context object is unstable
  }, [
    reviewPeriodsInWindow,
    debouncedWindowStart,
    debouncedWindowEnd,
    isEventsInSync,
    startIsInGapWithinWindow,
    resolveChainScope,
    eventContext.allEvents,
    eventContext.clearLocalChanges,
    eventContext.rememberSavedEvents,
    eventContext.modifiedEventIds,
    eventContext.originalEventsById,
    confirmSaveWarnings,
    persistVerifiedPeriods,
    previewVerification,
    reportChainCapped,
  ]);

  const prevWindowRef = useRef({
    start: viewingWindowStart,
    end: viewingWindowEnd,
  });
  if (
    !prevWindowRef.current.start.isSame(viewingWindowStart) ||
    !prevWindowRef.current.end.isSame(viewingWindowEnd)
  ) {
    prevWindowRef.current = {
      start: viewingWindowStart,
      end: viewingWindowEnd,
    };
    setIsEditMode(false);
  }

  useEffect(() => {
    eventContext.clearLocalChanges();
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- only run on window change, not on context reference change
  }, [viewingWindowStart, viewingWindowEnd]);

  const verifyWindowAndGoToNext = useMemo(
    () =>
      runExclusively(
        shouldRouteToSubmitEdits({
          unverifiedPeriodCount: unverifiedPeriodsInWindow.length,
          verifyPeriodStartCount: verifyPeriodStarts?.length ?? 0,
          hasEdits,
          hasGapEdits,
        })
          ? submitEditsImpl
          : verifyWindowAndGoToNextImpl
      ),
    [
      runExclusively,
      unverifiedPeriodsInWindow,
      verifyPeriodStarts,
      hasEdits,
      hasGapEdits,
      submitEditsImpl,
      verifyWindowAndGoToNextImpl,
    ]
  );
  const submitEdits = useMemo(
    () => runExclusively(submitEditsImpl),
    [runExclusively, submitEditsImpl]
  );
  const cancelEditMode = useMemo(
    () => runExclusively(cancelEditModeImpl),
    [runExclusively, cancelEditModeImpl]
  );

  return {
    verifyWindowAndGoToNext,
    handleUnverifyWindow,
    hasVerifiedPeriods,
    hasEdits,
    isEventsInSync,
    isEditMode,
    enterEditMode,
    cancelEditMode,
    submitEdits,
    isBringInChainRunning,
  };
};
